import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AppShell } from '../components/AppShell';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { HardHat, Plus, Loader2, ChevronRight, Calendar } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function VisitesGlobalPage() {
  const navigate = useNavigate();
  const [chantiers, setChantiers] = useState([]);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedChantier, setSelectedChantier] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [cRes, vRes] = await Promise.all([
          axios.get(`${API}/chantiers`, { withCredentials: true }),
          axios.get(`${API}/site-visits`, { withCredentials: true }),
        ]);
        setChantiers(cRes.data || []);
        setVisits(vRes.data || []);
      } catch { toast.error('Erreur de chargement'); }
      finally { setLoading(false); }
    })();
  }, []);

  const chantierById = (id) => chantiers.find(c => c.id === id);

  const createNew = async () => {
    if (!selectedChantier) { toast.error('Choisissez un chantier'); return; }
    setCreating(true);
    try {
      const r = await axios.post(`${API}/site-visits`, {
        chantier_id: selectedChantier,
        visit_date: new Date().toISOString().slice(0, 10),
        visit_time: new Date().toTimeString().slice(0, 5),
      }, { withCredentials: true });
      toast.success(`Visite n°${r.data.visit_number} créée`);
      navigate(`/chantiers/${selectedChantier}/site-visits`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setCreating(false); }
  };

  if (loading) return (
    <AppShell>
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#0055FF]" />
      </div>
    </AppShell>
  );

  return (
    <AppShell>
      <div className="space-y-5 max-w-5xl mx-auto" data-testid="visites-global-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-[#111827] flex items-center gap-2">
              <HardHat className="text-yellow-600" /> Visites de chantier
            </h1>
            <p className="text-sm text-[#6B7280] mt-0.5">
              {visits.length} visite{visits.length !== 1 ? 's' : ''} · Toutes vos visites terrain, tous chantiers confondus
            </p>
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-[#0055FF] hover:bg-blue-700" data-testid="global-new-visit-btn">
            <Plus size={16} className="mr-1.5" /> Nouvelle visite
          </Button>
        </div>

        {visits.length === 0 ? (
          <Card className="border-dashed border-2 border-[#D1D5DB]">
            <CardContent className="p-10 text-center">
              <HardHat size={48} className="mx-auto text-[#9CA3AF] mb-3" />
              <p className="text-sm text-[#6B7280] mb-1">Aucune visite de chantier enregistrée</p>
              <p className="text-xs text-[#9CA3AF] mb-4">
                Créez votre première visite pour documenter observations, non-conformités et actions terrain.
              </p>
              <Button onClick={() => setShowCreate(true)} className="bg-[#0055FF] hover:bg-blue-700">
                <Plus size={16} className="mr-1.5" /> Créer ma première visite
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {visits.map(v => {
              const c = chantierById(v.chantier_id);
              return (
                <Card key={v.id} className="hover:shadow-sm transition-shadow cursor-pointer"
                  onClick={() => navigate(`/chantiers/${v.chantier_id}/site-visits`)}
                  data-testid={`global-visit-${v.id}`}>
                  <CardContent className="p-3 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-yellow-50 flex items-center justify-center flex-shrink-0">
                      <HardHat size={20} className="text-yellow-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-[#111827]">
                          Visite n°{v.visit_number}
                        </span>
                        <span className="text-sm text-[#4B5563] truncate">
                          · {c?.name || v.chantier_name || '(chantier supprimé)'}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${v.status === 'finalized' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                          {v.status === 'finalized' ? '✓ Finalisée' : 'Brouillon'}
                        </span>
                        {(v.non_conformities || []).length > 0 && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">
                            ⚠️ {v.non_conformities.length} NC
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[#6B7280] flex items-center gap-2 mt-0.5">
                        <Calendar size={12} /> {v.visit_date} {v.visit_time && `· ${v.visit_time}`}
                        {v.phase && <span>· 🏗️ {v.phase}</span>}
                        {v.visitor_name && <span>· 👤 {v.visitor_name}</span>}
                      </div>
                    </div>
                    <ChevronRight size={20} className="text-gray-400 flex-shrink-0" />
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent aria-describedby="new-visit-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HardHat className="text-yellow-600" size={20} /> Nouvelle visite de chantier
            </DialogTitle>
            <p id="new-visit-desc" className="text-sm text-[#6B7280]">
              Sur quel chantier souhaitez-vous créer cette visite ?
            </p>
          </DialogHeader>
          {chantiers.length === 0 ? (
            <div className="text-sm text-[#6B7280] p-3 bg-amber-50 border border-amber-200 rounded-lg">
              Vous n&apos;avez encore aucun chantier. Créez-en un d&apos;abord dans la section Chantiers.
            </div>
          ) : (
            <>
              <Select value={selectedChantier} onValueChange={setSelectedChantier}>
                <SelectTrigger className="h-11" data-testid="chantier-picker">
                  <SelectValue placeholder="Sélectionner un chantier" />
                </SelectTrigger>
                <SelectContent>
                  {chantiers.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}{c.client_name ? ` — ${c.client_name}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex gap-2 justify-end pt-3">
                <Button variant="outline" onClick={() => setShowCreate(false)}>Annuler</Button>
                <Button onClick={createNew} disabled={!selectedChantier || creating}
                  className="bg-[#0055FF] hover:bg-blue-700" data-testid="create-visit-btn">
                  {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus size={14} className="mr-1" />}
                  Créer la visite
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
